import { createSlice } from "@reduxjs/toolkit";

import {
  fetchSessionInfos,
  fetchSessionInfoById,
  addSessionInfo,
  updateSessionInfo,
  deleteSessionInfo,
} from "./sessionInfo.thunk";

/* ============================================================
   NOTE ON CHANGES
   - Everything below is the same as before EXCEPT the pending
     cases of addSessionInfo and updateSessionInfo (marked
     "CHANGED"). They no longer set `loading = true`, so the
     table behind the modal does not flash its skeleton while a
     save is running. The modal uses its own `saving` state.
   - List filters (date range, student) live in the component and
     are sent through the fetch thunk, so no new state is needed.
============================================================ */

const initialState = {
  sessionInfos: [],
  totalPages: 1,
  currentPage: 1,
  total: 0,
  limit: 10,
  loading: false,
  error: null,
  selectedSessionInfo: null,
};

const sessionInfoSlice = createSlice({
  name: "sessionInfo",

  initialState,

  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    setSelectedSessionInfo: (state, action) => {
      state.selectedSessionInfo = action.payload;
    },

    clearSelectedSessionInfo: (state) => {
      state.selectedSessionInfo = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ============================================================
         FETCH SESSION INFO LIST
         Works with the optional filters (date_from, date_to,
         student_id): the API returns the same data + meta shape.
      ============================================================ */

      .addCase(fetchSessionInfos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchSessionInfos.fulfilled, (state, action) => {
        state.loading = false;
        // Each item now also carries lessons[] and attendance_roster
        state.sessionInfos = action.payload?.data || [];
        state.totalPages = action.payload?.meta?.total_pages || 1;
        state.total = action.payload?.meta?.total || 0;
        // API now returns meta.limit; falls back to 10 for older responses
        state.limit = action.payload?.meta?.limit || 10;
      })

      .addCase(fetchSessionInfos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         FETCH SINGLE SESSION INFO (DETAILED INSTANCE WITH ROSTER)
         Includes lessons[] and attendance_roster
      ============================================================ */

      .addCase(fetchSessionInfoById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchSessionInfoById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSessionInfo = action.payload;
      })

      .addCase(fetchSessionInfoById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         ADD SESSION INFO
      ============================================================ */

      .addCase(addSessionInfo.pending, (state) => {
        // CHANGED: `loading` is no longer set here. The list is not
        // being reloaded yet, so the table should stay visible.
        state.error = null;
      })

      .addCase(addSessionInfo.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(addSessionInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         UPDATE SESSION INFO
      ============================================================ */

      .addCase(updateSessionInfo.pending, (state) => {
        // CHANGED: same reason as add. The modal shows its own
        // "Saving…" state while the transaction runs.
        state.error = null;
      })

      .addCase(updateSessionInfo.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(updateSessionInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         DELETE SESSION INFO
      ============================================================ */

      .addCase(deleteSessionInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(deleteSessionInfo.fulfilled, (state, action) => {
        state.loading = false;

        // Ensure state list handles array filters safely
        const targetId = action.payload?.id;
        state.sessionInfos = (state.sessionInfos || []).filter(
          (instance) => instance.id !== targetId
        );
        if (state.selectedSessionInfo?.id === targetId) {
          state.selectedSessionInfo = null;
        }
      })

      .addCase(deleteSessionInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrentPage,
  setSelectedSessionInfo,
  clearSelectedSessionInfo,
} = sessionInfoSlice.actions;

export default sessionInfoSlice.reducer;